import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from '../hooks/useToast';
import { masterListApi } from '../utils/api';

// ─── Edit Modal ─────────────────────────────────────────────────────────────
function EditRowModal({ row, onClose, onSave }) {
  const toast = useToast();
  const [email, setEmail] = useState(row.email || '');
  const [host, setHost] = useState(row.host || '');
  const [notes, setNotes] = useState(row.notes || '');
  const [categories, setCategories] = useState(row.categories || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    const fields = {};
    if (email !== (row.email || '')) fields.email = email;
    if (host !== (row.host || '')) fields.host = host;
    if (notes !== (row.notes || '')) fields.notes = notes;
    if (categories !== (row.categories || '')) fields.categories = categories;

    if (!Object.keys(fields).length) { onClose(); return; }

    setSaving(true);
    try {
      const res = await masterListApi.update(row.rowNum, fields, row.name);
      const { row: updatedRow, propagation } = res.data;
      onSave(updatedRow);

      const updatedClients = (propagation || []).filter(p => p.success && !p.skipped);
      const failedClients = (propagation || []).filter(p => !p.success);
      if (failedClients.length) {
        toast(`Saved, but couldn't update: ${failedClients.map(f => f.clientName).join(', ')}`, 'error');
      } else if (updatedClients.length) {
        toast(`Saved ✓ — also updated in ${updatedClients.map(c => c.clientName).join(', ')}`, 'success');
      } else {
        toast('Saved ✓', 'success');
      }
    } catch (e) {
      toast(e.response?.data?.error || 'Error saving', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        <div className="modal-header">
          <div>
            <h2>{row.name}</h2>
            {row.host && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Host: {row.host}</div>}
          </div>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>✕</button>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: -8, marginBottom: 16 }}>
          Changes here update the master sheet and automatically propagate to every client's pitch list that already has this podcast.
        </p>
        <div className="form-group">
          <label>Email</label>
          <input value={email} onChange={e => setEmail(e.target.value)} placeholder="host@example.com" autoFocus />
        </div>
        <div className="form-group">
          <label>Host Name</label>
          <input value={host} onChange={e => setHost(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Categories</label>
          <input value={categories} onChange={e => setCategories(e.target.value)} placeholder="Business, Health" />
        </div>
        <div className="form-group">
          <label>Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
        </div>
        <div className="flex-center gap-2" style={{ justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Master List Page ──────────────────────────────────────────────────
export default function MasterList() {
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editRow, setEditRow] = useState(null);
  const debounceRef = useRef(null);
  const isFirstRun = useRef(true);

  const load = useCallback((query) => {
    setLoading(true);
    masterListApi.search(query)
      .then(r => setRows(r.data.rows || []))
      .catch(() => toast('Could not load the master sheet', 'error'))
      .finally(() => setLoading(false));
  }, [toast]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      load(search);
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(search), 350);
    return () => clearTimeout(debounceRef.current);
  }, [search]);

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.name}" everywhere? This removes it from the master sheet AND every client's pitch list + sheet (their booking report, if it's there, is untouched). This can't be undone.`)) return;
    try {
      const res = await masterListApi.delete(row.rowNum, row.name);
      setRows(prev => prev.filter(r => r.rowNum !== row.rowNum));
      const failed = (res.data.clientResults || []).filter(r => !r.success);
      if (failed.length) toast(`Deleted, but couldn't update sheet for: ${failed.map(f => f.clientName).join(', ')}`, 'error');
      else toast('Deleted everywhere', 'success');
    } catch (e) {
      toast(e.response?.data?.error || 'Could not delete', 'error');
    }
  };

  return (
    <div className="page-content">
      <div className="page-header flex-between">
        <div>
          <h1>Master List</h1>
          <p>Browse, edit, and delete podcasts on the Podcast Matcher master sheet. Edits propagate to every client list that has the same podcast.</p>
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by podcast or host name..." style={{ maxWidth: 360 }} />
      </div>

      {loading ? (
        <div className="empty-state"><p>Loading...</p></div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">◇</div>
          <h3>{search ? 'No results' : 'No podcasts found'}</h3>
          <p>{search ? 'Try a different search' : 'The master sheet appears to be empty.'}</p>
        </div>
      ) : (
        <div className="table-wrap">
          {!search && rows.length >= 200 && (
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Showing the first 200 podcasts — search to find a specific one.</p>
          )}
          <table>
            <thead>
              <tr>
                <th>Podcast</th>
                <th>Host</th>
                <th>Email</th>
                <th>Categories</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.rowNum}>
                  <td>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{row.name}</div>
                    {row.link && <a href={row.link} target="_blank" rel="noreferrer" style={{ fontSize: 11, color: 'var(--accent)' }}>↗ Link</a>}
                  </td>
                  <td style={{ fontSize: 13 }}>{row.host || '—'}</td>
                  <td style={{ fontSize: 12 }}>
                    {row.email ? row.email : <span style={{ color: 'var(--amber)' }}>⚠ Missing</span>}
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.categories || '—'}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{row.status || '—'}</td>
                  <td>
                    <div className="flex-center gap-1">
                      <button className="btn btn-sm btn-secondary" onClick={() => setEditRow(row)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(row)}>Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editRow && (
        <EditRowModal
          row={editRow}
          onClose={() => setEditRow(null)}
          onSave={(updated) => {
            setRows(prev => prev.map(r => r.rowNum === updated.rowNum ? updated : r));
            setEditRow(null);
          }}
        />
      )}
    </div>
  );
}
